import { useFormState, useForm } from 'react-final-form';
import type { AnyObject, FormApi, ValidationErrors } from 'final-form';

/** Тип состояние формы в react-final-form */
type TFinalForm = FormApi<Record<string, unknown>>;

/** Функция вычисления значения прогресс-бара */
type TValueCalculator = (form: TFinalForm, initValue?: number) => number;

/** Функция вычисления значения прогресс-бара */
type TFindInvalid = (errors: ValidationErrors, form: FormApi<AnyObject>) => string;

const calculateProgress: TValueCalculator = (form, initValue = 0) => {
    const state = form.getState();

    const allFields = form.getRegisteredFields();

    const successFields = allFields.filter(value => {
        return Boolean(!state.errors[value] && state.values[value]);
    });

    const calculateValue = (acc: number, cField: string): number => {
        const data = form.getFieldState(cField)?.data;
        const progressBarValue = data?.progressBarValue ?? data?.settings?.progressBarValue;

        return acc + (progressBarValue || /* istanbul ignore next */ 0);
    };

    const value = successFields.reduce(calculateValue, 0);

    return initValue + value;
};

const findInvalid: TFindInvalid = (errors, form) =>
    form.getRegisteredFields().find(field => {
        const data = form.getFieldState(field)?.data;

        const isError = errors[field] !== undefined;
        const isState = data?.progressBarValue ?? data?.settings?.progressBarValue;

        return isError && isState;
    }) ?? '';

/** Хук вычисляет значение заполненности прогресс-бара и выбирает нужный плейсхолдер для текста над/под линией прогресс-бара */
export const useProgress = (initValue?: number) => {
    const { active = '', errors } = useFormState({
        subscription: { active: true, errors: true },
    });
    const form = useForm();
    const fieldData = form.getFieldState(active)?.data ?? {};

    const isProgressBarValue = fieldData.progressBarValue ?? fieldData?.settings?.progressBarValue;

    // При наличии поля и ошибок в поле и настроек для прогресс-бара - проставлять поле
    const newField = errors[active] && isProgressBarValue ? active : findInvalid(errors, form);

    const data = form.getFieldState(newField)?.data ?? {};

    const value = calculateProgress(form, initValue);

    const progressBarText = data?.progressBarText ?? data?.settings?.progressBarText;
    const placeholder = data?.placeholder ?? data?.settings?.placeholder;
    const progressBarValue = data?.progressBarValue ?? data?.settings?.progressBarValue;

    return { placeholder: progressBarText || placeholder, progressBarValue, value };
};
